import { useEffect } from 'react';

import clsx from 'clsx';

import {
  BuyerIntentLeadClick,
  normalizeBuyerIntentId,
  warnMissingBuyerIntentIds,
} from 'components/BuyerIntent';
import Card from 'components/Card';
import Grid from 'components/Grid';
import Link from 'components/Link';
import Typography from 'components/Typography';
import { useAppContext } from 'context/AppContext';
import { useEventContext } from 'context/EventContext';
import { useListingDetailContext } from 'context/ListingDetailContext';
import useViewport from 'hooks/useViewport';
import useLasForm from 'screens/serp/hooks/useLasForm';

import advisorsImg from 'components/PPLPricingBanner/assets/advisors.png';

import classes from './PPLBannerVariant.module.scss';

const PPLBannerVariant = ({ className, show, ...props }) => {
  const { addEvent, sendEvent } = useEventContext();
  const { head } = useAppContext();
  const { listing } = useListingDetailContext();
  const { up } = useViewport();
  const isDesktop = up('md');
  let headDataLayer = head?.data_layer;

  useEffect(() => {
    if (show) {
      sendEvent({
        engagementComponent: 'ppl_banner',
        engagementDestination: 'banner_displayed',
        engagementDetails: 'scrolled-to_ppl_banner',
        name: 'section_impression',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // NOTE: necessary to prevent 404
  if (typeof headDataLayer !== 'undefined') {
    headDataLayer = head?.data_layer[0];
  }
  const globalCategoryId = headDataLayer?.categoryPageUuid;
  const buyerIntentProductId = normalizeBuyerIntentId(
    listing.global_product_id
  );
  const buyerIntentCategoryId = normalizeBuyerIntentId(globalCategoryId);

  const wrapBuyerIntentLeadClick = (content, sourceLocation) => {
    if (!buyerIntentProductId) {
      warnMissingBuyerIntentIds('listing-detail/ppl-banner:demo', {
        productId: listing.global_product_id,
        categoryId: globalCategoryId,
      });

      return content;
    }

    return (
      <BuyerIntentLeadClick
        productId={buyerIntentProductId}
        categoryId={buyerIntentCategoryId}
        sourceLocation={sourceLocation}
      >
        {content}
      </BuyerIntentLeadClick>
    );
  };

  const callfunc = useLasForm({
    hasPplExitIntentEnabled: true,
    global_category_id: globalCategoryId,
    isPPL: true,
    config: {
      pGuid: listing.global_product_id,
      formTemplate: 'demo',
    },
    form: 'openLasModal',
    hasCallFunction: true,
    enabled: show,
  });

  const content = (
    <Card
      className={classes.cardGradient}
      elevation={1}
      onClick={() => {
        !isDesktop && callfunc();
      }}
      {...(isDesktop
        ? {}
        : addEvent({
            engagementComponent: 'ppl_banner',
            engagementDestination: 'go-to_ppl-demo-form',
            engagementDetails: 'mobile_card_click',
            name: 'ppl_cta',
          }))}
    >
      <Grid container={true}>
        <Grid
          item={true}
          xs={5}
          sm={3}
          md={12}
          className={classes.usersContainer}
        >
          {isDesktop ? (
            wrapBuyerIntentLeadClick(
              // biome-ignore lint/nursery/noImgElement: static asset, no Next.js Image needed
              <img
                src={advisorsImg.src}
                alt=""
                onClick={() => {
                  callfunc();
                }}
                {...addEvent({
                  engagementComponent: 'ppl_banner',
                  engagementDestination: 'go-to_ppl-demo-form',
                  engagementDetails: 'image_click',
                  name: 'ppl_cta',
                })}
              />,
              'listing-detail/ppl-banner:demo-image'
            )
          ) : (
            // biome-ignore lint/nursery/noImgElement: static asset, no Next.js Image needed
            <img src={advisorsImg.src} alt="" />
          )}
        </Grid>

        <Grid item={true} xs={7} sm={7} md={12}>
          <Typography variant="h3" className={classes.title}>
            Is this product right for your business?
          </Typography>
          <Typography className={classes.subTitle}>
            Find out with a{' '}
            {isDesktop
              ? wrapBuyerIntentLeadClick(
                  <Link
                    onClick={() => {
                      callfunc();
                    }}
                    {...addEvent({
                      engagementComponent: 'ppl_banner',
                      engagementDestination: 'go-to_ppl-demo-form',
                      engagementDetails: 'link_free-demo',
                      name: 'ppl_cta',
                    })}
                  >
                    Free Demo
                  </Link>,
                  'listing-detail/ppl-banner:demo-link'
                )
              : 'free Demo'}
          </Typography>
        </Grid>
      </Grid>
    </Card>
  );

  return (
    <Grid
      className={clsx(
        'PPLBanner',
        classes.root,
        {
          [classes.animate]: show,
        },
        className
      )}
      {...props}
    >
      {isDesktop
        ? content
        : wrapBuyerIntentLeadClick(content, 'listing-detail/ppl-banner:demo')}
    </Grid>
  );
};

export default PPLBannerVariant;
